import { useAction, useQuery } from "convex/react";
import { useState } from "react";

import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/Button";
import { LobsterIcon } from "@/components/ui/LobsterIcon";
import { CodeInput } from "./CodeInput";

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_CODE: "That code doesn't look right. Check and try again.",
  CODE_REVOKED: "This code has been revoked.",
  CODE_EXPIRED: "This code has expired.",
  CODE_MAXED: "This code has reached its usage limit.",
  ALREADY_ACTIVATED: "Your account is already activated.",
  SIGNUPS_DISABLED: "Signups are currently closed.",
};

interface SignupCodeInterstitialProps {
  onGoBack: () => void;
}

export function SignupCodeInterstitial({ onGoBack }: SignupCodeInterstitialProps) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isRedeeming, setIsRedeeming] = useState(false);

  const validation = useQuery(
    api.signupCodes.validateSignupCode,
    code.length === 5 ? { code } : "skip",
  );
  const redeemCode = useAction(api.signupCodes.redeemSignupCode);

  const isValid = validation?.accepted === true;
  const validationError = validation?.accepted === false ? validation.reason : null;

  async function handleRedeem() {
    if (!isValid) return;
    setIsRedeeming(true);
    setError(null);

    try {
      const result = await redeemCode({ code });
      if (!result.success) {
        setError(ERROR_MESSAGES[result.errorCode ?? ""] ?? "Something went wrong.");
      }
      // On success, the getMe query will reactively update and AuthGuard will let us through
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsRedeeming(false);
    }
  }

  const displayError = error ?? (validationError ? ERROR_MESSAGES[validationError] ?? validationError : null);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-light p-6 dark:bg-surface-dark">
      <div className="w-full max-w-md space-y-6 rounded-3xl border border-black bg-white p-10 text-center dark:border-white/20 dark:bg-[#2A2A2A]">
        <LobsterIcon className="mx-auto h-16 w-16 text-brand-coral" />
        <div>
          <h1 className="font-heading text-2xl text-black dark:text-white">
            Enter your access code
          </h1>
          <p className="mt-2 text-sm text-black/50 dark:text-white/50">
            Clawcraft is in alpha. Enter your invite code to continue.
          </p>
        </div>

        <CodeInput
          value={code}
          onChange={(c) => { setCode(c); setError(null); }}
          disabled={isRedeeming}
          error={!!displayError}
        />

        {displayError && (
          <p className="text-sm text-red-500 dark:text-red-400">{displayError}</p>
        )}

        <Button
          onClick={() => void handleRedeem()}
          disabled={!isValid || isRedeeming}
          className="w-full"
          size="lg"
        >
          {isRedeeming ? "Activating..." : "Continue"}
        </Button>

        <button
          onClick={onGoBack}
          className="text-sm text-black/40 underline hover:text-black/60 dark:text-white/40 dark:hover:text-white/60"
        >
          Go back
        </button>
      </div>
    </div>
  );
}
